'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type RefObject, useEffect, useEffectEvent, useRef, useState } from 'react';
import { applyRowRange, getAutoScrollStep, getRowRangeIds } from '../components/row-selection-range';
import type { Row, RowData, RowSelectionState, Table } from '../tanstack-table';
export type RowSelectionGestures = {
  /** Forgets the selection anchor, so the next Shift gesture selects one row. */
  clearAnchor: () => void;
  getSelectionCellProps: (row: GestureRow, checkboxRef: RefObject<HTMLElement | null>) => Pick<React.HTMLAttributes<HTMLElement>, 'onClickCapture' | 'onMouseDown' | 'onPointerDown'>;
  /** Makes a row toggled through the checkbox itself, by keyboard or touch, the anchor. */
  setAnchor: (rowId: string) => void;
};

/** Range and drag selection for the selection cells of the enclosing table. */
export const RowSelectionGesturesContext = createContext<null | RowSelectionGestures>(null);
type DragSession = {
  anchorId: string;
  /** The selection this drag last wrote, to tell its own writes from outside ones. */
  applied: RowSelectionState;
  clientX: number;
  clientY: number;
  isOverridden: boolean;
  lastRowId: string;
  pointerId: number;
  /** Whether the sweep selects or deselects the rows it passes. */
  selected: boolean;
  snapshot: RowSelectionState;
};
type GestureRow = Pick<Row, 'getCanSelect' | 'getIsSelected' | 'id'>;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
type Bounds = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};
const isPrimaryMousePress = (event: React.PointerEvent) => event.button === 0 && event.pointerType === 'mouse';

/**
 * Range selection (Shift+click or Shift+Space from the selection anchor) and drag selection
 * (press a row checkbox and sweep over other rows) for one table's loaded rows.
 *
 * A mouse press commits the gesture immediately and suppresses its trailing click, so the
 * checkbox never toggles twice. Keyboard, touch and pen keep the checkbox's own toggle.
 */
export const useRowSelectionGestures = t0 => {
  const $ = _c(40);
  const {
    enabled,
    getViewport,
    table
  } = t0;
  const anchorIdRef = useRef(null);
  const sessionRef = useRef(null);
  const autoScrollFrameRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (autoScrollFrameRef.current !== null) {
        cancelAnimationFrame(autoScrollFrameRef.current);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== table) {
    t3 = () => {
      const anchorId = anchorIdRef.current;
      if (anchorId === null || !table.getRowModel().rowsById[anchorId]) {
        return null;
      }
      return Object.keys(table.atoms.rowSelection.get()).length > 0 ? anchorId : null;
    };
    $[2] = table;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getAnchorId = t3;
  let t4;
  if ($[4] !== table) {
    t4 = (base, anchorId_0, targetId, selected) => {
      const {
        rows,
        rowsById
      } = table.getRowModel();
      const rangeIds = getRowRangeIds(rows.map(_temp), anchorId_0, targetId).filter(id => rowsById[id]?.getCanSelect());
      const next = applyRowRange(base, rangeIds, selected, new Set(Object.keys(rowsById)));
      table.setRowSelection(next);
      return next;
    };
    $[4] = table;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectRange = t4;
  const getRowBounds = _temp2;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (session, viewport_0) => {
      const bounds_0 = getRowBounds(viewport_0);
      const x = clamp(session.clientX, bounds_0.left + 1, bounds_0.right - 1);
      const y = clamp(session.clientY, bounds_0.top + 1, bounds_0.bottom - 1);
      for (const element of document.elementsFromPoint(x, y)) {
        const rowElement = element.closest("[data-row-id]");
        if (rowElement?.dataset.rowId && viewport_0.contains(rowElement)) {
          return rowElement;
        }
      }
      return null;
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const getRowAtPointer = t5;
  let t6;
  if ($[7] !== getViewport || $[8] !== selectRange || $[9] !== table.atoms) {
    t6 = session_0 => {
      if (session_0.isOverridden) {
        return;
      }
      const selection = table.atoms.rowSelection.get();
      if (selection !== session_0.applied && Object.keys(selection).length === 0) {
        session_0.isOverridden = true;
        return;
      }
      const viewport_1 = getViewport();
      const rowElement_0 = viewport_1 ? getRowAtPointer(session_0, viewport_1) : null;
      const rowId = rowElement_0?.dataset.rowId;
      if (!rowElement_0 || !rowId || rowId === session_0.lastRowId) {
        return;
      }
      session_0.lastRowId = rowId;
      session_0.applied = selectRange(session_0.snapshot, session_0.anchorId, rowId, session_0.selected);
      rowElement_0.querySelector("[data-table-selection-checkbox]")?.focus({
        preventScroll: true
      });
    };
    $[7] = getViewport;
    $[8] = selectRange;
    $[9] = table.atoms;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const extendDrag = t6;
  let autoScroll;
  if ($[11] !== extendDrag || $[12] !== getViewport) {
    autoScroll = () => {
      autoScrollFrameRef.current = null;
      const session_1 = sessionRef.current;
      const viewport_2 = getViewport();
      if (!session_1 || session_1.isOverridden || !viewport_2) {
        return;
      }
      const step = getAutoScrollStep(session_1.clientY, getRowBounds(viewport_2));
      if (step === 0) {
        return;
      }
      const scrollTop = viewport_2.scrollTop;
      viewport_2.scrollTop = viewport_2.scrollTop + step;
      if (viewport_2.scrollTop !== scrollTop) {
        extendDrag(session_1);
      }
      autoScrollFrameRef.current = requestAnimationFrame(autoScroll);
    };
    $[11] = extendDrag;
    $[12] = getViewport;
    $[13] = autoScroll;
  } else {
    autoScroll = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const session_2 = sessionRef.current;
      if (!session_2) {
        return;
      }
      sessionRef.current = null;
      setIsDragging(false);
      if (autoScrollFrameRef.current !== null) {
        cancelAnimationFrame(autoScrollFrameRef.current);
        autoScrollFrameRef.current = null;
      }
      if (!session_2.isOverridden) {
        anchorIdRef.current = session_2.lastRowId;
      }
      setTimeout(() => {
        if (!sessionRef.current) {
          suppressClickRef.current = false;
        }
      });
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const endDrag = t7;
  let t8;
  if ($[15] !== getAnchorId || $[16] !== selectRange || $[17] !== table.atoms) {
    t8 = (event, row_0) => {
      endDrag();
      const snapshot = table.atoms.rowSelection.get();
      const selected_0 = !snapshot[row_0.id];
      const anchorId_1 = (event.shiftKey ? getAnchorId() : null) ?? row_0.id;
      const session_3 = {
        anchorId: anchorId_1,
        applied: selectRange(snapshot, anchorId_1, row_0.id, selected_0),
        clientX: event.clientX,
        clientY: event.clientY,
        isOverridden: false,
        lastRowId: row_0.id,
        pointerId: event.pointerId,
        selected: selected_0,
        snapshot
      };
      sessionRef.current = session_3;
      suppressClickRef.current = true;
      setIsDragging(true);
    };
    $[15] = getAnchorId;
    $[16] = selectRange;
    $[17] = table.atoms;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const startDrag = t8;
  let t9;
  if ($[19] !== autoScroll || $[20] !== extendDrag) {
    t9 = event_0 => {
      const session_4 = sessionRef.current;
      if (!session_4 || event_0.pointerId !== session_4.pointerId) {
        return;
      }
      if ((event_0.buttons & 1) === 0) {
        endDrag();
        return;
      }
      session_4.clientX = event_0.clientX;
      session_4.clientY = event_0.clientY;
      extendDrag(session_4);
      if (autoScrollFrameRef.current === null) {
        autoScrollFrameRef.current = requestAnimationFrame(autoScroll);
      }
    };
    $[19] = autoScroll;
    $[20] = extendDrag;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const onDragPointerMove = useEffectEvent(t9);
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event_1 => {
      if (event_1.pointerId === sessionRef.current?.pointerId) {
        endDrag();
      }
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const onDragPointerEnd = useEffectEvent(t10);
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => endDrag();
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const onDragBlur = useEffectEvent(t11);
  let t12;
  if ($[24] !== isDragging || $[25] !== onDragBlur || $[26] !== onDragPointerEnd || $[27] !== onDragPointerMove) {
    t12 = () => {
      if (!isDragging) {
        return;
      }
      const handlePointerMove = event_2 => onDragPointerMove(event_2);
      const handlePointerEnd = event_3 => onDragPointerEnd(event_3);
      const handleBlur = () => onDragBlur();
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerEnd);
      window.addEventListener("pointercancel", handlePointerEnd);
      window.addEventListener("blur", handleBlur);
      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerEnd);
        window.removeEventListener("pointercancel", handlePointerEnd);
        window.removeEventListener("blur", handleBlur);
      };
    };
    $[24] = isDragging;
    $[25] = onDragBlur;
    $[26] = onDragPointerEnd;
    $[27] = onDragPointerMove;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== isDragging) {
    t13 = [isDragging];
    $[29] = isDragging;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  useEffect(t12, t13);
  if (!enabled) {
    return null;
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      anchorIdRef.current = null;
    };
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== getAnchorId || $[33] !== selectRange || $[34] !== startDrag || $[35] !== table.atoms) {
    t15 = (row_1, checkboxRef) => ({
      onClickCapture: event_4 => {
        if (event_4.target instanceof HTMLInputElement) {
          return;
        }
        if (suppressClickRef.current) {
          event_4.preventDefault();
          event_4.stopPropagation();
          return;
        }
        const anchorId_2 = event_4.shiftKey ? getAnchorId() : null;
        if (anchorId_2 === null || !row_1.getCanSelect()) {
          return;
        }
        event_4.preventDefault();
        event_4.stopPropagation();
        selectRange(table.atoms.rowSelection.get(), anchorId_2, row_1.id, !row_1.getIsSelected());
        anchorIdRef.current = row_1.id;
      },
      onMouseDown: event_5 => {
        if (sessionRef.current) {
          event_5.preventDefault();
        }
      },
      onPointerDown: event_6 => {
        if (!isPrimaryMousePress(event_6) || !row_1.getCanSelect()) {
          return;
        }
        event_6.preventDefault();
        checkboxRef.current?.focus({
          preventScroll: true
        });
        startDrag(event_6, row_1);
      }
    });
    $[32] = getAnchorId;
    $[33] = selectRange;
    $[34] = startDrag;
    $[35] = table.atoms;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = rowId_0 => {
      anchorIdRef.current = rowId_0;
    };
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t15) {
    t17 = {
      clearAnchor: t14,
      getSelectionCellProps: t15,
      setAnchor: t16
    };
    $[38] = t15;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};
function _temp(row) {
  return row.id;
}
function _temp2(viewport) {
  const bounds = viewport.getBoundingClientRect();
  const top = Math.max(bounds.top, viewport.querySelector("thead")?.getBoundingClientRect().bottom ?? 0);
  const bottom = Math.min(bounds.bottom, viewport.querySelector("tfoot")?.getBoundingClientRect().top ?? bounds.bottom);
  return bottom > top ? {
    bottom,
    left: bounds.left,
    right: bounds.right,
    top
  } : bounds;
}